import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  FileUp, FileDown, Loader2, ArrowRight, CheckCircle2, AlertTriangle, RotateCcw,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

/**
 * /admin/csv  — Import / Export venues via CSV.
 *
 * Three steps for import: (1) upload + preview, (2) confirm column mapping,
 * (3) run + see per-row outcome. Export side is a single form that hits
 * GET /api/admin/data-tools/csv-export with selected columns + filters.
 */
export default function AdminCsvImport() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("import");

  return (
    <div data-testid="admin-csv-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
          {tab === "import" ? <FileUp className="h-6 w-6 text-primary" /> :
           tab === "export" ? <FileDown className="h-6 w-6 text-primary" /> :
           <Package className="h-6 w-6 text-primary" />}
          {t("admin_csv.title", { defaultValue: "Bulk CSV / ZIP" })}
        </h1>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          <Button
            size="sm"
            variant={tab === "import" ? "default" : "ghost"}
            onClick={() => setTab("import")}
            data-testid="csv-tab-import"
          >
            {t("admin_csv.tab_import", { defaultValue: "CSV Import" })}
          </Button>
          <Button
            size="sm"
            variant={tab === "export" ? "default" : "ghost"}
            onClick={() => setTab("export")}
            data-testid="csv-tab-export"
          >
            {t("admin_csv.tab_export", { defaultValue: "CSV Export" })}
          </Button>
          <Button
            size="sm"
            variant={tab === "zip" ? "default" : "ghost"}
            onClick={() => setTab("zip")}
            data-testid="csv-tab-zip"
          >
            {t("admin_csv.tab_zip", { defaultValue: "ZIP Bundle" })}
          </Button>
        </div>
      </div>

      {tab === "import" ? <ImportFlow /> : tab === "export" ? <ExportFlow /> : <ZipFlow />}
    </div>
  );
}

/* ============== IMPORT FLOW ============== */
function ImportFlow() {
  const { t } = useTranslation();
  const [step, setStep] = useState("upload"); // upload | map | running | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [venueFields, setVenueFields] = useState([]);
  const [options, setOptions] = useState({
    create_missing_districts: true,
    create_missing_areas: true,
    create_missing_cuisines: true,
    skip_duplicates: true,
    default_plan_key: "",
    photo_index_filter: "all",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/data-tools/csv-fields`)
      .then((r) => setVenueFields(r.data.venue_fields || []))
      .catch(() => {});
    axios.get(`${API}/admin/venue-plans`)
      .then((r) => setPlans(r.data || []))
      .catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/data-tools/csv-preview`, fd);
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setStep("map");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to parse CSV");
    } finally {
      setBusy(false);
    }
  };

  const handleRun = async () => {
    setBusy(true); setStep("running");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("options", JSON.stringify(options));
      const { data } = await axios.post(`${API}/admin/data-tools/csv-import`, fd);
      setResult(data);
      setStep("done");
      toast.success(t("admin_csv.toast_done", {
        defaultValue: "Imported {{created}}, skipped {{skipped}}, errors {{errors}}",
        ...data.summary,
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
      setStep("map");
    } finally {
      setBusy(false);
    }
  };

  const handleReset = () => {
    setStep("upload"); setFile(null); setPreview(null); setMapping({}); setResult(null);
  };

  const requiredFields = useMemo(
    () => venueFields.filter((f) => f.required).map((f) => f.key),
    [venueFields],
  );
  const mappedTargets = new Set(Object.values(mapping).filter(Boolean));
  const missingRequired = requiredFields.filter((k) => !mappedTargets.has(k));

  return (
    <div className="space-y-6">
      <Stepper current={step} />

      {step === "upload" && (
        <>
          <form onSubmit={handleUpload} className="bg-card rounded-xl border border-border p-6 space-y-4 max-w-xl"
            data-testid="csv-upload-form">
            <p className="text-sm text-muted-foreground">
              {t("admin_csv.upload_intro", {
                defaultValue: "Upload a CSV with one venue per row. Multi-value fields (cuisines, photo URLs) use ; (semicolon) as delimiter. Max 5 MB / 5,000 rows.",
              })}
            </p>
            <div>
              <Label>{t("admin_csv.csv_file", { defaultValue: "CSV file" })}</Label>
              <Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1" data-testid="csv-file-input" required />
            </div>
            <Button type="submit" disabled={!file || busy} data-testid="csv-upload-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              {t("admin_csv.parse_btn", { defaultValue: "Parse & preview" })}
            </Button>
          </form>
          {/* Always-on storage panel so admins can monitor or trigger
              backfill even without re-running an import. */}
          <PhotoStoragePanel />
        </>
      )}

      {step === "map" && preview && (
        <div className="space-y-4">
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="font-medium text-foreground mb-3">
              {t("admin_csv.map_title", { defaultValue: "Map CSV columns to venue fields" })}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              {t("admin_csv.map_subtitle", {
                defaultValue: "{{n}} rows detected. Required fields are marked with *. Leave a column unmapped to skip it.",
                n: preview.row_count,
              })}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {preview.headers.map((h) => (
                <div key={h} className="flex items-center gap-2">
                  <span className="text-sm font-mono w-1/2 truncate" title={h}>{h}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Select
                    value={mapping[h] || "__skip__"}
                    onValueChange={(v) => setMapping({ ...mapping, [h]: v === "__skip__" ? "" : v })}
                  >
                    <SelectTrigger className="flex-1" data-testid={`csv-map-${h}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__skip__">{t("admin_csv.map_skip", { defaultValue: "— Skip —" })}</SelectItem>
                      {venueFields.map((f) => (
                        <SelectItem key={f.key} value={f.key}>
                          {f.label}{f.required ? " *" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="font-medium text-foreground mb-3">{t("admin_csv.options_title", { defaultValue: "Options" })}</h3>
            <div className="space-y-2 text-sm">
              <Toggle label={t("admin_csv.create_districts", { defaultValue: "Auto-create missing districts" })}
                value={options.create_missing_districts}
                onChange={(v) => setOptions({ ...options, create_missing_districts: v })}
                testId="csv-opt-districts" />
              <Toggle label={t("admin_csv.create_areas", { defaultValue: "Auto-create missing areas" })}
                value={options.create_missing_areas}
                onChange={(v) => setOptions({ ...options, create_missing_areas: v })}
                testId="csv-opt-areas" />
              <Toggle label={t("admin_csv.create_cuisines", { defaultValue: "Auto-create missing cuisines" })}
                value={options.create_missing_cuisines}
                onChange={(v) => setOptions({ ...options, create_missing_cuisines: v })}
                testId="csv-opt-cuisines" />
              <Toggle label={t("admin_csv.skip_duplicates", { defaultValue: "Skip duplicates (same slug + district + area)" })}
                value={options.skip_duplicates}
                onChange={(v) => setOptions({ ...options, skip_duplicates: v })}
                testId="csv-opt-skip-dups" />
              <div className="flex items-center gap-3 pt-2">
                <Label className="m-0">{t("admin_csv.default_plan", { defaultValue: "Default plan for rows without a plan column" })}</Label>
                <Select value={options.default_plan_key || "__none__"}
                  onValueChange={(v) => setOptions({ ...options, default_plan_key: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="w-56" data-testid="csv-opt-default-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("admin_csv.no_default", { defaultValue: "— None (legacy) —" })}</SelectItem>
                    {plans.map((p) => <SelectItem key={p.plan_key} value={p.plan_key}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Label className="m-0">
                  {t("admin_csv.photo_filter", {
                    defaultValue: "Gallery photos to keep per row (use to skip duplicates at different sizes)",
                  })}
                </Label>
                <Select
                  value={options.photo_index_filter}
                  onValueChange={(v) => setOptions({ ...options, photo_index_filter: v })}
                >
                  <SelectTrigger className="w-56" data-testid="csv-opt-photo-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("admin_csv.photo_filter_all", { defaultValue: "All photos" })}
                    </SelectItem>
                    <SelectItem value="even">
                      {t("admin_csv.photo_filter_even", { defaultValue: "Even-indexed only (1st, 3rd, 5th…)" })}
                    </SelectItem>
                    <SelectItem value="odd">
                      {t("admin_csv.photo_filter_odd", { defaultValue: "Odd-indexed only (2nd, 4th, 6th…)" })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <PreviewTable preview={preview} mapping={mapping} />

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleReset} data-testid="csv-reset-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> {t("admin_csv.start_over", { defaultValue: "Start over" })}
            </Button>
            <Button onClick={handleRun} disabled={busy || missingRequired.length > 0}
              data-testid="csv-run-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {t("admin_csv.run_btn", { defaultValue: "Run import" })}
            </Button>
            {missingRequired.length > 0 && (
              <span className="text-xs text-destructive flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                {t("admin_csv.missing_required", {
                  defaultValue: "Missing required: {{fields}}",
                  fields: missingRequired.join(", "),
                })}
              </span>
            )}
          </div>
        </div>
      )}

      {step === "running" && (
        <div className="bg-card rounded-xl border border-border p-10 text-center">
          <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {t("admin_csv.running", { defaultValue: "Importing rows..." })}
          </p>
        </div>
      )}

      {step === "done" && result && (
        <ResultPanel result={result} onReset={handleReset} />
      )}
    </div>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} />
      <span className="text-foreground">{label}</span>
    </label>
  );
}

function Stepper({ current }) {
  const steps = [
    { key: "upload", label: "1. Upload" },
    { key: "map", label: "2. Map columns" },
    { key: "done", label: "3. Result" },
  ];
  return (
    <div className="flex items-center gap-2 text-sm">
      {steps.map((s, i) => {
        const active = current === s.key || (current === "running" && s.key === "map");
        const done = (current === "done" && s.key !== "done") || (current === "map" && s.key === "upload");
        return (
          <div key={s.key} className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded-full text-xs ${
              active ? "bg-primary text-primary-foreground" :
              done ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"
            }`}>
              {s.label}
            </span>
            {i < steps.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
          </div>
        );
      })}
    </div>
  );
}

function PreviewTable({ preview, mapping }) {
  const { t } = useTranslation();
  const headers = preview.headers;
  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="px-6 py-3 border-b border-border">
        <p className="text-sm font-medium text-foreground">
          {t("admin_csv.preview", { defaultValue: "Preview (first 20 rows)" })}
        </p>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {headers.map((h) => (
                <TableHead key={h} className="whitespace-nowrap">
                  {h}
                  {mapping[h] && <span className="block text-[10px] text-primary font-mono">→ {mapping[h]}</span>}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {preview.preview_rows.map((row, idx) => (
              <TableRow key={idx}>
                {headers.map((h) => <TableCell key={h} className="whitespace-nowrap text-xs max-w-[240px] truncate">{row[h]}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ResultPanel({ result, onReset }) {
  const { t } = useTranslation();
  const s = result.summary || {};
  return (
    <div className="space-y-4" data-testid="csv-result-panel">
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <p className="font-medium text-foreground">{t("admin_csv.done_title", { defaultValue: "Import complete" })}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
          <Stat label={t("admin_csv.stat_created", { defaultValue: "Created" })} value={s.created} colour="text-emerald-600" />
          <Stat label={t("admin_csv.stat_skipped", { defaultValue: "Skipped" })} value={s.skipped} colour="text-amber-600" />
          <Stat label={t("admin_csv.stat_errors", { defaultValue: "Errors" })} value={s.errors} colour="text-rose-600" />
          <Stat label={t("admin_csv.stat_districts", { defaultValue: "+ Districts" })} value={s.districts_created} />
          <Stat label={t("admin_csv.stat_areas", { defaultValue: "+ Areas" })} value={s.areas_created} />
          <Stat label={t("admin_csv.stat_cuisines", { defaultValue: "+ Cuisines" })} value={s.cuisines_created} />
        </div>
        {s.photos_queued > 0 && (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="csv-photos-queued">
            {t("admin_csv.photos_queued", {
              defaultValue: "{{count}} photos are downloading to object storage in the background. Track progress in the Photo Storage panel.",
              count: s.photos_queued,
            })}
          </p>
        )}
      </div>

      <PhotoStoragePanel />

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="px-6 py-3 border-b border-border">
          <p className="text-sm font-medium text-foreground">{t("admin_csv.row_log", { defaultValue: "Row-by-row outcome" })}</p>
        </div>
        <div className="max-h-96 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Row</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Slug / Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(result.rows || []).map((r) => (
                <TableRow key={r.row}>
                  <TableCell>{r.row}</TableCell>
                  <TableCell>
                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                      r.status === "created" ? "bg-emerald-100 text-emerald-700" :
                      r.status === "skipped" ? "bg-amber-100 text-amber-700" :
                      "bg-rose-100 text-rose-700"
                    }`}>{r.status}</span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.slug || r.message || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Button onClick={onReset} variant="outline" data-testid="csv-result-reset">
        <RotateCcw className="h-4 w-4 mr-2" /> {t("admin_csv.import_more", { defaultValue: "Import another CSV" })}
      </Button>
    </div>
  );
}

function Stat({ label, value, colour }) {
  return (
    <div className="bg-muted/40 rounded-lg p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-medium ${colour || "text-foreground"}`}>{value ?? 0}</p>
    </div>
  );
}

/**
 * Surfaces the storage_status counts of CSV-imported venue_images and lets
 * the admin (re)trigger the download-to-object-storage backfill. Useful both
 * after a fresh import (to watch the queue drain) and for legacy CSV imports
 * whose photos were never persisted as binaries.
 */
function PhotoStoragePanel() {
  const { t } = useTranslation();
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/data-tools/csv-photos/status`);
      setCounts(data);
    } catch (e) {
      // Endpoint optional — silently ignore if unavailable
    }
  };

  // Poll every 2s while there's work in flight (pending or unqueued legacy
  // rows), otherwise fall back to a slow 10s heartbeat. This makes the
  // progress bar feel live while a backfill is running without hammering
  // the API once everything is settled.
  useEffect(() => {
    refresh();
    const hasWork =
      counts && ((counts.pending || 0) > 0 || (counts.legacy || 0) + (counts.external || 0) > 0);
    const interval = hasWork ? 2000 : 10000;
    const id = setInterval(refresh, interval);
    return () => clearInterval(id);
  }, [counts?.pending, counts?.legacy, counts?.external]);

  const runBackfill = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/data-tools/csv-photos/backfill`,
        null,
        { params: { limit: 1000, retry_failed: retryFailed } },
      );
      toast.success(
        t("admin_csv.backfill_queued", {
          defaultValue: "Queued {{count}} photos for download.",
          count: data.queued,
        }),
      );
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Backfill failed");
    } finally {
      setLoading(false);
    }
  };

  if (!counts) return null;
  const pending = counts.pending || 0;
  const stored = counts.stored || 0;
  const failed = counts.failed || 0;
  const legacy = (counts.legacy || 0) + (counts.external || 0);
  const total = counts.total || 0;
  if (total === 0) return null;
  const completedPct = total > 0 ? Math.round(((stored + failed) / total) * 100) : 0;
  const inFlight = pending > 0 || legacy > 0;

  return (
    <div
      className={`bg-card rounded-xl border p-6 transition-colors ${
        inFlight ? "border-primary/50" : "border-border"
      }`}
      data-testid="csv-photo-storage-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-medium text-foreground flex items-center gap-2">
            {t("admin_csv.photo_storage_title", { defaultValue: "Photo storage" })}
            {inFlight && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("admin_csv.photo_storage_hint", {
              defaultValue: "CSV-imported photos are downloaded to Emergent object storage so the gallery serves real binaries instead of remote hotlinks.",
            })}
          </p>
        </div>
        <Button
          size="sm"
          onClick={runBackfill}
          disabled={loading || (pending === 0 && legacy === 0 && (!retryFailed || failed === 0))}
          data-testid="csv-photo-backfill-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
          {t("admin_csv.photo_backfill_run", { defaultValue: "Download missing photos" })}
        </Button>
      </div>

      {/* Progress bar — segmented stored / failed / pending so the operator
          can see at a glance how much work is left. */}
      <div className="mb-4" data-testid="csv-photo-progress">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>
            {t("admin_csv.photo_progress_label", {
              defaultValue: "{{stored}} of {{total}} stored",
              stored,
              total,
            })}
          </span>
          <span className="tabular-nums">{completedPct}%</span>
        </div>
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden flex">
          {total > 0 && (
            <>
              <div
                className="h-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${(stored / total) * 100}%` }}
              />
              <div
                className="h-full bg-rose-500 transition-all duration-500"
                style={{ width: `${(failed / total) * 100}%` }}
              />
              <div
                className={`h-full bg-amber-400 transition-all duration-500 ${
                  pending > 0 ? "animate-pulse" : ""
                }`}
                style={{ width: `${(pending / total) * 100}%` }}
              />
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <Stat label={t("admin_csv.photo_total", { defaultValue: "Total" })} value={total} />
        <Stat label={t("admin_csv.photo_stored", { defaultValue: "Stored" })} value={stored} colour="text-emerald-600" />
        <Stat label={t("admin_csv.photo_pending", { defaultValue: "Downloading" })} value={pending} colour="text-amber-600" />
        <Stat label={t("admin_csv.photo_failed", { defaultValue: "Failed" })} value={failed} colour="text-rose-600" />
        <Stat label={t("admin_csv.photo_legacy", { defaultValue: "Not yet queued" })} value={legacy} />
      </div>
      <label className="flex items-center gap-2 mt-3 text-xs text-muted-foreground" data-testid="csv-photo-retry-toggle">
        <input
          type="checkbox"
          checked={retryFailed}
          onChange={(e) => setRetryFailed(e.target.checked)}
        />
        {t("admin_csv.photo_retry_failed", { defaultValue: "Retry previously failed photos" })}
      </label>
    </div>
  );
}

/* ============== EXPORT FLOW ============== */
function ExportFlow() {
  const { t } = useTranslation();
  const [columns, setColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [delimiter, setDelimiter] = useState(",");
  const [districts, setDistricts] = useState([]);
  const [districtFilter, setDistrictFilter] = useState("__all__");
  const [planFilter, setPlanFilter] = useState("__all__");
  const [activeFilter, setActiveFilter] = useState("__all__");
  const [plans, setPlans] = useState([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/data-tools/csv-export/columns`),
      axios.get(`${API}/taxonomy/districts`),
      axios.get(`${API}/admin/venue-plans`),
    ]).then(([cols, dists, pls]) => {
      setAllColumns(cols.data.columns.map((c) => c.key));
      setColumns(cols.data.default);
      setDistricts(dists.data || []);
      setPlans(pls.data || []);
    }).catch(() => {});
  }, []);

  const toggleColumn = (k) => {
    setColumns(columns.includes(k) ? columns.filter((c) => c !== k) : [...columns, k]);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("columns", columns.join(","));
      params.set("delimiter", delimiter === "tab" ? "\t" : delimiter);
      if (districtFilter !== "__all__") params.set("district_id", districtFilter);
      if (planFilter !== "__all__") params.set("venue_plan_key", planFilter);
      if (activeFilter !== "__all__") params.set("is_active", activeFilter);
      const res = await axios.get(`${API}/admin/data-tools/csv-export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `tablecy-venues-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("admin_csv.export_done", { defaultValue: "CSV downloaded" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Export failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6 space-y-5 max-w-3xl" data-testid="csv-export-form">
      <p className="text-sm text-muted-foreground">
        {t("admin_csv.export_intro", {
          defaultValue: "Pick the columns you want and (optionally) filter by district / plan / active. The export round-trips back into the import.",
        })}
      </p>

      <div>
        <Label className="mb-2 block">{t("admin_csv.export_columns", { defaultValue: "Columns" })} ({columns.length})</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {allColumns.map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm bg-muted/40 rounded-lg p-2 cursor-pointer hover:bg-muted/70 transition">
              <input type="checkbox" checked={columns.includes(k)} onChange={() => toggleColumn(k)}
                data-testid={`csv-col-${k}`} />
              <span className="font-mono text-xs">{k}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label>{t("admin_csv.delimiter", { defaultValue: "Delimiter" })}</Label>
          <Select value={delimiter} onValueChange={setDelimiter}>
            <SelectTrigger className="mt-1" data-testid="csv-export-delimiter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value=",">,  (comma)</SelectItem>
              <SelectItem value=";">;  (semicolon)</SelectItem>
              <SelectItem value="tab">⇥  (tab)</SelectItem>
              <SelectItem value="|">|  (pipe)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("admin_csv.filter_district", { defaultValue: "District" })}</Label>
          <Select value={districtFilter} onValueChange={setDistrictFilter}>
            <SelectTrigger className="mt-1" data-testid="csv-export-district"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("admin_csv.all_districts", { defaultValue: "All districts" })}</SelectItem>
              {districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("admin_csv.filter_plan", { defaultValue: "Plan" })}</Label>
          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="mt-1" data-testid="csv-export-plan"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("admin_csv.all_plans", { defaultValue: "All plans" })}</SelectItem>
              {plans.map((p) => <SelectItem key={p.plan_key} value={p.plan_key}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("admin_csv.filter_active", { defaultValue: "Active" })}</Label>
          <Select value={activeFilter} onValueChange={setActiveFilter}>
            <SelectTrigger className="mt-1" data-testid="csv-export-active"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("admin_csv.all", { defaultValue: "All" })}</SelectItem>
              <SelectItem value="true">{t("admin_csv.active_only", { defaultValue: "Active only" })}</SelectItem>
              <SelectItem value="false">{t("admin_csv.inactive_only", { defaultValue: "Inactive only" })}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button onClick={handleDownload} disabled={downloading || columns.length === 0}
        data-testid="csv-export-download">
        {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
        {t("admin_csv.export_btn", { defaultValue: "Download CSV" })}
      </Button>
    </div>
  );
}


/* ============== ZIP FLOW (Phase 7) ============== */
function ZipFlow() {
  const { t } = useTranslation();
  const [exportFetchPhotos, setExportFetchPhotos] = useState(true);
  const [exportDistrict, setExportDistrict] = useState("__all__");
  const [exportPlan, setExportPlan] = useState("__all__");
  const [districts, setDistricts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/taxonomy/districts`),
      axios.get(`${API}/admin/venue-plans`),
    ]).then(([d, p]) => { setDistricts(d.data || []); setPlans(p.data || []); })
      .catch(() => {});
  }, []);

  const handleExport = async () => {
    setExportBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("fetch_photos", exportFetchPhotos ? "true" : "false");
      if (exportDistrict !== "__all__") params.set("district_id", exportDistrict);
      if (exportPlan !== "__all__") params.set("venue_plan_key", exportPlan);
      const res = await axios.get(`${API}/admin/data-tools/zip-export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tablecy-zip-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("admin_csv.zip_export_done", { defaultValue: "ZIP downloaded" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setExportBusy(false);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    setImportBusy(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("options", JSON.stringify({
        create_missing_districts: true,
        create_missing_areas: true,
        create_missing_cuisines: true,
      }));
      const { data } = await axios.post(`${API}/admin/data-tools/zip-import`, fd);
      setImportResult(data);
      toast.success(t("admin_csv.zip_import_done", { defaultValue: "ZIP imported" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="zip-flow">
      {/* Export panel */}
      <section className="bg-card rounded-xl border border-border p-6 space-y-4">
        <h3 className="font-medium text-foreground flex items-center gap-2">
          <FileDown className="h-5 w-5 text-primary" />
          {t("admin_csv.zip_export_title", { defaultValue: "Export ZIP bundle" })}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("admin_csv.zip_export_intro", {
            defaultValue:
              "Bundle venues + photo manifest into a single ZIP. Optionally download photo binaries so the archive is fully self-contained.",
          })}
        </p>
        <Toggle
          label={t("admin_csv.zip_fetch_photos", { defaultValue: "Download photo binaries (heavier)" })}
          value={exportFetchPhotos}
          onChange={setExportFetchPhotos}
          testId="zip-fetch-photos"
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t("admin_csv.filter_district", { defaultValue: "District" })}</Label>
            <Select value={exportDistrict} onValueChange={setExportDistrict}>
              <SelectTrigger className="mt-1" data-testid="zip-export-district"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("admin_csv.all_districts", { defaultValue: "All districts" })}</SelectItem>
                {districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("admin_csv.filter_plan", { defaultValue: "Plan" })}</Label>
            <Select value={exportPlan} onValueChange={setExportPlan}>
              <SelectTrigger className="mt-1" data-testid="zip-export-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("admin_csv.all_plans", { defaultValue: "All plans" })}</SelectItem>
                {plans.map((p) => <SelectItem key={p.plan_key} value={p.plan_key}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={handleExport} disabled={exportBusy} data-testid="zip-export-btn">
          {exportBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
          {t("admin_csv.zip_export_btn", { defaultValue: "Download ZIP" })}
        </Button>
      </section>

      {/* Import panel */}
      <section className="bg-card rounded-xl border border-border p-6 space-y-4">
        <h3 className="font-medium text-foreground flex items-center gap-2">
          <FileUp className="h-5 w-5 text-primary" />
          {t("admin_csv.zip_import_title", { defaultValue: "Import ZIP bundle" })}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("admin_csv.zip_import_intro", {
            defaultValue:
              "Upload a previously-exported TableCy ZIP. Existing venues (same slug + district + area) are skipped automatically; new ones are created. Photo binaries are wired up via the manifest.",
          })}
        </p>
        <form onSubmit={handleImport} className="space-y-3">
          <div>
            <Label>{t("admin_csv.zip_file", { defaultValue: "ZIP file" })}</Label>
            <input
              type="file" accept=".zip,application/zip"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm"
              data-testid="zip-import-file"
              required
            />
          </div>
          <Button type="submit" disabled={!importFile || importBusy} data-testid="zip-import-btn">
            {importBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />}
            {t("admin_csv.zip_import_btn", { defaultValue: "Run ZIP import" })}
          </Button>
        </form>
        {importResult && (
          <div className="bg-muted/40 rounded-lg p-3 text-sm space-y-1" data-testid="zip-import-result">
            <p className="font-medium">{t("admin_csv.zip_import_summary", { defaultValue: "Summary" })}</p>
            {Object.entries(importResult.summary || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs">
                <span className="font-mono text-muted-foreground">{k}</span>
                <span className="font-medium">{v}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
